import { ContentSkeleton } from '@/components/loading/Skeleton'
import { useCallback, useEffect, useRef, useState } from 'react'
import { CircleAlert, Route, Star, Trash2, Upload } from 'lucide-react'
import { api } from '@/util/api'
import { Badge, Button, Checkbox, ConfirmDialog, IconButton, Notice, Progress, TextField, useToast } from '@/components/play'
import { useEventChannel } from '@/hooks/useEventChannel'
import { workspaceError } from '@/lib/workspace'
import { LivelabsMapsImport } from './LivelabsMapsImport'
import type {
  EventRoute,
  EventRoutesIndexResponse,
  EventRouteResponse,
} from '@/types/events'

interface Props {
  eventId: string
}

interface ApiError {
  response?: { data?: { error?: { errors?: string[]; message?: string } } }
}

/** What the server is doing with the course, shown as a progress bar until it's done. */
function work(route: EventRoute): string | null {
  if (route.parse_status === 'parsing') return 'Preparing the course'
  if (route.world_status === 'building') return 'Building the world'
  return null
}

/**
 * The later of two copies of a course. A socket message and a load can cross (a load that started before a build
 * landed answers after its message), so the copy that changed last wins.
 */
function newer(current: EventRoute | undefined, incoming: EventRoute): EventRoute {
  if (!current?.updated_at || !incoming.updated_at) return incoming
  return Date.parse(current.updated_at) > Date.parse(incoming.updated_at) ? current : incoming
}

/** The courses with one added or changed: a new primary course comes first and the others stop being primary. */
function withCourse(routes: EventRoute[], incoming: EventRoute): EventRoute[] {
  const index = routes.findIndex((each) => each.id === incoming.id)
  const route = newer(routes[index], incoming)
  if (route !== incoming) return routes
  const others = routes.filter((each) => each.id !== route.id).map((each) => (route.primary && each.primary ? { ...each, primary: false } : each))
  if (route.primary) return [route, ...others]
  if (index === -1) return [...others, route]
  return [...others.slice(0, index), route, ...others.slice(index)]
}

/** A load of the courses, keeping any course a message has changed since the load was read. */
function fromLoad(routes: EventRoute[], loaded: EventRoute[]): EventRoute[] {
  return loaded.map((route) => newer(routes.find((each) => each.id === route.id), route))
}

function statusLabel(route: EventRoute): string {
  const bits: string[] = []
  if (route.parse_status === 'parsed') bits.push('Course ready')
  if (route.parse_status === 'failed') bits.push('Needs attention')
  if (route.distance_km != null) bits.push(`${route.distance_km.toFixed(1)} km`)
  if (route.source === 'livelabs_maps') bits.push('From LiveLabs Maps')
  if (route.world_status === 'ready') bits.push('World ready')
  if (route.world_status === 'failed') bits.push('World not built')
  return bits.join(' · ')
}

export function EventCourseForm({ eventId }: Props) {
  const { toast } = useToast()
  const [routes, setRoutes] = useState<EventRoute[]>([])
  const [loading, setLoading] = useState(true)
  const [uploading, setUploading] = useState(false)
  const [name, setName] = useState('')
  const [primary, setPrimary] = useState(false)
  const [file, setFile] = useState<File | null>(null)
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)
  // The target outlives `removalOpen` so the confirmation keeps its title while it closes.
  const [removal, setRemoval] = useState<EventRoute | null>(null)
  const [removalOpen, setRemovalOpen] = useState(false)
  const [removalError, setRemovalError] = useState('')

  const load = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const res = await api.get<EventRoutesIndexResponse>(
        `/events/${eventId}/routes`,
      )
      setRoutes((now) => fromLoad(now, res.data))
    } catch (failure) {
      setError(
        workspaceError(failure, 'Your courses couldn’t load. Try again.'),
      )
    } finally {
      setLoading(false)
    }
  }, [eventId])

  useEffect(() => {
    void load()
  }, [load])

  // The event's workspace channel says when a course is added, changes (its parse, its world's build) or goes, here
  // and wherever else this form is open. After the socket drops, the courses load again, quietly.
  useEventChannel(eventId, {
    scope: 'workspace',
    on: {
      'route.created': (data) => setRoutes((now) => withCourse(now, data as EventRoute)),
      'route.updated': (data) => setRoutes((now) => withCourse(now, data as EventRoute)),
      'route.deleted': (data) => setRoutes((now) => now.filter((route) => route.id !== (data as { id: string }).id)),
    },
    onResync: () => {
      api.get<EventRoutesIndexResponse>(`/events/${eventId}/routes`).then(
        (res) => setRoutes((now) => fromLoad(now, res.data)),
        () => {},
      )
    },
  })

  // Each course's world as last shown, to tell when a build lands or fails. Builds of the event's courses finish one
  // after another, so "World ready" waits for the last.
  const worlds = useRef<Map<string, Pick<EventRoute, 'world_status' | 'world_built_at'>> | null>(null)
  const landed = useRef(false)
  useEffect(() => {
    if (loading) return
    const before = worlds.current
    worlds.current = new Map(routes.map((route) => [route.id, { world_status: route.world_status, world_built_at: route.world_built_at }]))
    if (!before) return
    const failed = routes.find((route) => route.world_status === 'failed' && before.get(route.id)?.world_status !== 'failed')
    if (routes.some((route) => route.world_status === 'ready' && route.world_built_at && route.world_built_at !== before.get(route.id)?.world_built_at)) {
      landed.current = true
    }
    if (failed) {
      landed.current = false
      toast({ title: 'World not built', description: failed.world_error || 'The world couldn’t be built.', tone: 'error' })
    } else if (landed.current && !routes.some((route) => work(route) !== null)) {
      landed.current = false
      toast({ title: 'World ready', description: 'Your event map shows the new world.', tone: 'success' })
    }
  }, [loading, routes, toast])

  async function upload(e: React.FormEvent) {
    e.preventDefault()
    if (!file || uploading) return
    setUploading(true)
    try {
      const body = new FormData()
      body.append('route[name]', name || 'Default route')
      body.append('route[primary]', String(primary))
      body.append('gpx', file)
      await api.postMultipart<EventRouteResponse>(
        `/events/${eventId}/routes`,
        body,
      )
      setName('')
      setPrimary(false)
      setFile(null)
      toast({
        title: 'Course added',
        description: 'Your route is ready for the event map.',
        tone: 'success',
      })
      await load()
    } catch (err: unknown) {
      const apiErr = err as ApiError
      const message =
        apiErr.response?.data?.error?.errors?.join('\n') ??
        apiErr.response?.data?.error?.message ??
        'The course couldn’t be uploaded. Check the GPX file and try again.'
      toast({
        title: 'Course not added',
        description: message,
        tone: 'error',
      })
    } finally {
      setUploading(false)
    }
  }

  async function promote(route: EventRoute) {
    if (busy) return
    setBusy(true)
    setError('')
    try {
      await api.patch<EventRouteResponse>(
        `/events/${eventId}/routes/${route.id}`,
        { route: { primary: true } },
      )
      await load()
    } catch (failure) {
      setError(
        workspaceError(
          failure,
          'The primary course couldn’t be changed. Try again.',
        ),
      )
    } finally {
      setBusy(false)
    }
  }

  // Confirmed in a ConfirmDialog, which stays loading until this settles and keeps a failure in view.
  async function remove(route: EventRoute) {
    setBusy(true)
    setRemovalError('')
    try {
      await api.delete<void>(`/events/${eventId}/routes/${route.id}`)
    } catch (failure) {
      setRemovalError(
        workspaceError(failure, 'The course couldn’t be removed. Try again.'),
      )
      throw failure
    } finally {
      setBusy(false)
    }
    await load()
  }

  return (
    <div className="space-y-5">
      {loading && (
        <ContentSkeleton label="Loading courses" variant="list" count={2} />
      )}
      {error && (
        <Notice
          tone="error"
          alert
          action={
            <Button variant="secondary" size="compact" onClick={() => void load()}>
              Try again
            </Button>
          }
        >
          {error}
        </Notice>
      )}
      {!loading && routes.length > 0 && (
        <div className="space-y-2">
          {routes.map((route) => (
            <div
              key={route.id}
              className="flex items-center gap-3 py-3"
            >
              <Route className="h-4 w-4 text-neutral-7" aria-hidden="true" />
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="truncate text-sm font-medium">
                    {route.name}
                  </span>
                  {route.primary && <Badge tone="accent">Primary</Badge>}
                </div>
                {statusLabel(route) && (
                  <div className="text-xs text-neutral-7">
                    {statusLabel(route)}
                  </div>
                )}
                {work(route) && (
                  <Progress
                    className="mt-xxs max-w-xs"
                    label={work(route)}
                    value={null}
                    showValue={false}
                    inline
                  />
                )}
                {route.parse_error && (
                  <div className="flex items-center gap-xxs text-xs text-error">
                    <CircleAlert className="h-3 w-3 shrink-0" aria-hidden="true" />
                    {route.parse_error}
                  </div>
                )}
                {route.world_status === 'failed' && route.world_error && (
                  <div className="flex items-center gap-xxs text-xs text-error">
                    <CircleAlert className="h-3 w-3 shrink-0" aria-hidden="true" />
                    {route.world_error}
                  </div>
                )}
                {route.source_warnings?.map((warning) => (
                  <div key={warning} className="text-xs text-neutral-7">
                    {warning}
                  </div>
                ))}
              </div>
              {!route.primary && (
                <IconButton
                  label={`Make “${route.name}” the primary course`}
                  variant="ghost"
                  onClick={() => void promote(route)}
                  disabled={busy}
                >
                  <Star />
                </IconButton>
              )}
              <IconButton
                label={`Remove “${route.name}”`}
                variant="ghost"
                onClick={() => {
                  setRemoval(route)
                  setRemovalError('')
                  setRemovalOpen(true)
                }}
                disabled={busy}
              >
                <Trash2 />
              </IconButton>
            </div>
          ))}
        </div>
      )}

      <ConfirmDialog
        open={removalOpen}
        onOpenChange={setRemovalOpen}
        title={`Remove “${removal?.name ?? ''}”?`}
        description="It comes off your event map."
        confirmLabel="Remove course"
        onConfirm={() => (removal ? remove(removal) : undefined)}
        failureMessage={removalError || undefined}
      />

      <LivelabsMapsImport eventId={eventId} onImported={() => void load()} />

      <form onSubmit={upload} className="space-y-6">
        <TextField
          id="route-name"
          label="Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. 100 km"
        />
        <TextField
          id="route-gpx"
          label="GPX file"
          type="file"
          accept=".gpx,application/gpx+xml,application/xml,text/xml"
          onChange={(e) => setFile(e.target.files?.[0] ?? null)}
        />
        <Checkbox
          label="Make this the primary course"
          checked={primary}
          onCheckedChange={(checked) => setPrimary(checked)}
        />
        <Button type="submit" disabled={!file} loading={uploading}>
          <Upload aria-hidden="true" />
          Upload course
        </Button>
      </form>
    </div>
  )
}
